import { Box, Typography } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';

interface PaletteEdgeTabProps {
  label: string;
  ariaLabel: string;
  icon: React.ReactNode;
  isNarrow: boolean;
  onOpen: () => void;
}

/**
 * Just the pill's own look — position and stacking (equal gaps, anchored to
 * match the open panel) are owned by the flex container in BlockPaletteEdgeTabs.
 */
function tabSx(isNarrow: boolean) {
  const common = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    bgcolor: Palette.default.buttonBg,
    color: Palette.default.buttonText,
    cursor: 'pointer',
  };
  if (isNarrow) {
    return {
      ...common,
      minHeight: 40,
      px: 1.75,
      py: 1.25,
      borderRadius: 999,
      boxShadow: 4,
    };
  }
  return {
    ...common,
    flexDirection: 'column' as const,
    px: 0.75,
    py: 1.5,
    borderRadius: '0 10px 10px 0',
    boxShadow: 3,
    transition: 'padding-left 0.15s ease',
    '&:hover': { pl: 1.25 },
  };
}

/** A slim trigger ("+ CONTENT", "MEDIA") shown while the sidebar is closed; one per tab. */
export default function PaletteEdgeTab({
  label,
  ariaLabel,
  icon,
  isNarrow,
  onOpen,
}: PaletteEdgeTabProps) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      sx={tabSx(isNarrow)}
    >
      {icon}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: '0.08em',
          ...(isNarrow ? {} : { writingMode: 'vertical-rl' }),
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
